import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import useWeb3 from '../../../hooks/useWeb3'
import { Container } from '../styled'
import { Box } from '../../../shared_components/UI/Box'
import ImageSVG from '../../../shared_components/ImageSVG'
import { IDrop } from '../../../types/reducerTypes'
import useFetchAndStoreAllDropsData from '../../../hooks/drops/useFetchAndStoreAllDropsData'
import { useLazyGetAllDropsQuery } from '../../../modules/listDrops/api'
import { isEmptyOrNil } from '../../../utils/IsEmptyOrNil'
import DropCard, { OpeningLabel } from '../../Drops/DropCard'
import UpcomingPoolLoadingState from '../parts/LoadingStates/Upcoming'
import { DropStatus, getDropDateStatus } from '../../DropDetails/helpers'
import styled from 'styled-components'
import HowItWorks, { Card } from '../../../shared_components/HowItWorks'
import { DropsHero } from '../../../shared_components/Library/Hero'
import { Grid } from '../../../shared_components/Library/Grid'
import { ChipNavButton } from '../../DropDetails'
import { Strong } from '../../../shared_components/Library/Typeography'
import { toTitleCase } from '../../../theme'
import { SUPPORTED_CHAINS } from '../../../data/supported-chains'
import { isNotNil } from 'ramda'

const DROPS_CHAINS = ['base', 'blast']

export default function Home() {
  const [activeFilteredChain, setActiveFilteredChain] = useState('')

  const { poolsData } = useWeb3()
  useEffect(() => {
    ;(async () => {
      await poolsData.dispatchPoolsToStore('labs')
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [drops, setDrops] = useState<IDrop[]>([])

  const dispatchAllDropsToStore = useFetchAndStoreAllDropsData()
  useEffect(() => {
    dispatchAllDropsToStore({})
  }, [dispatchAllDropsToStore])

  const [fetchAllDrops, { isLoading }] = useLazyGetAllDropsQuery()

  useEffect(() => {
    fetchAllDrops({})
      .unwrap()
      .then(({ drops }) => {
        const groupedByParentDrop = drops.reduce((acc, drop) => {
          if (!drop.parentDropId) return acc
          acc[drop.parentDropId] = [...(acc?.[drop.parentDropId] ?? []), drop]
          return acc
        }, {}) as Record<number, IDrop[]>

        // Sort the drops within each group based on status and ID
        Object.keys(groupedByParentDrop).forEach((parentDropId) => {
          groupedByParentDrop[parentDropId] = groupedByParentDrop[parentDropId]
            .sort((a, b) => a.id - b.id)
            .map((drop, index) => ({ ...drop, chapterIndex: index }))

          groupedByParentDrop[parentDropId]
            .sort((a, b) => {
              const statusOrder = {
                [DropStatus.Live]: 0,
                [DropStatus.ComingSoon]: 1,
                [DropStatus.TBA]: 2,
                [DropStatus.Closed]: 3,
              }

              const statusA = getDropDateStatus(a)
              const statusB = getDropDateStatus(b)

              if (statusA === statusB) {
                return statusA === DropStatus.Closed ? b.id - a.id : a.id - b.id
              }

              return statusOrder[statusA] - statusOrder[statusB]
            })
            .map((drop, index) => ({ ...drop, chapterIndex: index }))
        })
        const latestDrops = Object.keys(groupedByParentDrop)
          .map((key) => {
            const dropsByParentDrop = groupedByParentDrop[Number(key)]
            return dropsByParentDrop[0] // The first drop after sorting will be the desired one
          })
          .sort((a, b) => {
            const aOrder = a?.parentDrop?.order
            const bOrder = b?.parentDrop?.order

            if (isNotNil(aOrder) && isNotNil(bOrder)) {
              // Both have order, compare by order
              return bOrder - aOrder
            } else if (isNotNil(aOrder)) {
              // Only a has order, sort a before b
              return 1
            } else if (isNotNil(bOrder)) {
              // Only b has order, sort b before a
              return -1
            }

            return 0
          })

        setDrops(latestDrops.reverse())
      })
  }, [fetchAllDrops])

  const hasDrops = !isEmptyOrNil(drops)

  const activeChainId = Object.keys(SUPPORTED_CHAINS).find((chainId) => {
    const chainDetails = SUPPORTED_CHAINS[chainId]
    return chainDetails?.chainName?.toLowerCase() === activeFilteredChain
  })

  const filteredDrops = !!activeChainId
    ? drops.filter((drop) => drop.chainId === String(activeChainId))
    : drops

  return (
    <Container>
      <DropsHero />
      <Box ac g={10} padB="m">
        <ChipNavButton
          $styleType={
            activeFilteredChain !== '' ? 'tertiary' : 'active-chip'
          }
          onClick={() => setActiveFilteredChain('')}
          bss={activeFilteredChain === '' ? 'focused' : 'enabled'}
          bst={activeFilteredChain === '' ? 'filled' : 'outlined'}
        >
          All
        </ChipNavButton>
        {DROPS_CHAINS.map((chain) => {
          const isFilterActive = activeFilteredChain === chain
          return (
            <ChipNavButton
              key={chain}
              $styleType={isFilterActive ? 'active-chip' : 'tertiary'}
              onClick={() => setActiveFilteredChain(chain)}
              bss={isFilterActive ? 'focused' : 'enabled'}
              bst={isFilterActive ? 'filled' : 'outlined'}
            >
              <Box ac g={4}>
                <img
                  src={`/img/icons/chains/color-circle/${chain}.svg`}
                  alt={chain + ' logo'}
                  className="chain-icon"
                />
                <Strong s="M">{toTitleCase(chain)}</Strong>
              </Box>
            </ChipNavButton>
          )
        })}
      </Box>
      {hasDrops && (
        <Grid fw ig padB="section">
          {filteredDrops?.map((drop) => (
            <DropCard key={drop.id} drop={drop} isListPage />
          ))}
          {isLoading &&
            Array(4)
              .fill(null)
              .map((_, i) => <UpcomingPoolLoadingState key={uuidv4()} />)}
        </Grid>
      )}
    </Container>
  )
}

export function ZapProductIcon() {
  return <ImageSVG src="/icon-zap.svg" />
}

export function LabsProductIcon() {
  return <img src="/icon-labs.svg" alt="ZAP Labs" />
}

export function LaunchProductIcon() {
  return <img src="/icon-launch.svg" alt="ZAP Launch" />
}

export function DropsProductIcon() {
  return <img src="/icon-drops.svg" alt="ZAP Drops" />
}

export const ProjectsTitle = styled(Box)`
  color: #fff;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 32px;
  font-style: normal;
  font-weight: 300;
  line-height: 25px; /* 78.125% */
  letter-spacing: 9.6px;
  text-transform: uppercase;
`

export const ProjectsTitleBox = styled(Box)`
  flex: unset;
  width: auto;

  ${OpeningLabel} {
    margin-left: 0;
    margin-right: 0;
  }

  @media screen and (max-width: 1000px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;

    * {
      justify-content: center;
      margin-left: auto;
      margin-right: auto;
    }
    ${OpeningLabel} {
      margin-top: 16px;
    }
    ${ProjectsTitle} {
      text-align: center;
      line-height: 1;
    }
  }
`

export const HighlightedGradientText = styled.h3`
  background: linear-gradient(279deg, #fff 19.04%, #fdfd0f 132.95%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

export function HowItWorksDrops() {
  return (
    <HowItWorks backgroundUrl="/howitworks-cover-drops.png">
      <Card
        imageUrl="/howitworks-card-1-drops.jpeg"
        title="Step 1"
        subTitle="Select"
        text="Early-stage crypto projects need your help to gain exposure. Find the best airdrops on ZAP Drops"
      />
      <Card
        imageUrl="/howitworks-card-2-drops.jpeg"
        title="Step 2"
        subTitle="Participate"
        text="Complete missions to earn points for each drop. From social amplification to on-chain activities - there's something for everyone"
      />
      <Card
        imageUrl="/howitworks-card-3-drops.jpeg"
        title="Step 3"
        subTitle="Claim"
        text="Clearly see your points, and how you stack up against the rest. Once the airdrop closes, claim your rewards in one click."
      />
    </HowItWorks>
  )
}
